import { PATH_DASHBOARD } from '../../../routes/paths';
import { TABLE_MENU_GROUPS } from '../../../constants/tableMenuLabels';
import { STAGE_ICONS } from '../../../constants/stageIcons';
import { SvgColor } from '../../../components/svg-color';
import Iconify from '../../../components/Iconify';
import DraftSessionBadge from '../../../components/annotations/DraftSessionBadge';
import TaskBadge from '../../../components/TaskBadge';
import WhatsappBadge from '../../../components/WhatsappBadge';

const getIcon = (name) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} />;

const ICONS = {
  blog: getIcon('ic_blog'),
  cart: getIcon('ic_cart'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  user: getIcon('ic_user'),
  kanban: getIcon('ic_kanban'),
  banking: getIcon('ic_banking'),
  job: getIcon('ic_job'),
  invoice: getIcon('ic_file'),
  tables: getIcon('ic_filter'),
  calendar: getIcon('ic_calendar'),
  ecommerce: getIcon('ic_ecommerce'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  menuItem: getIcon('ic_menu_item'),
  campaigns: getIcon('ic_campaign'),
  notification: getIcon('ic_notification'),
  task: getIcon('ic_task'),
  campaign: getIcon('ic_campaign'),
  financial: getIcon('ic_financial'),
  qrcode: getIcon('ic_qrcode'),
  facebook: getIcon('ic_facebook'),
  whatsapp: getIcon('ic_whatsapp'),
  sms: getIcon('ic_sms'),
  patientReview: getIcon('ic_label'),

  leadManagement: getIcon('ic_folder'),
};

// Maps TABLE_NAMES values (used as hidden_parent_tables entries) → nav child titles.
// Only entries that map to filterable nav children need to appear here.
const TABLE_NAME_TO_NAV_TITLE = {
  'Newest Leads': 'New',
  'Consultation Booked Leads': 'Booked',
  'Consultation Attended Leads': 'Attended',
  'Treatment Started Leads': 'Started',
  'Failed to attend Leads': 'FTA',
  'Completed Leads': 'Completed',
  'Archived Leads': 'Archived',
  'No Task Assigned': 'No Task Assigned',
  'Aligner Approve Leads': 'Aligner Approve',
};

// Table-item children for the Active Leads / Lead Management groups now live
// in tableMenuLabels.js (TABLE_MENU_GROUPS) so the sidebar and the lead-detail
// breadcrumb (menuCrumbsForOrigin) can never drift apart.
// Table-menu children carry a stage icon NAME (tableMenuLabels → STAGE_ICONS); the sidebar
// renders it as an 18px glyph. Children without one (the More submenus) keep the dot.
const withStageIcon = (item) => (item.icon ? { ...item, icon: <Iconify icon={item.icon} width={18} /> } : item);

const activeLeadsGroup = TABLE_MENU_GROUPS.find((g) => g.group === 'Active Leads');
const leadManagementGroup = TABLE_MENU_GROUPS.find((g) => g.group === 'Lead Management');

const getNavConfig = (practiceId, { isSuperAdmin = false, hasOwnDiary = false, hasMultiplePractices = false, hasTasksV2 = false, hiddenParentTables = [], hasAttendanceUi = false } = {}) => {
  const hiddenNavTitles = new Set(hiddenParentTables.map((t) => TABLE_NAME_TO_NAV_TITLE[t]).filter(Boolean));

  const activeLeadsChildren = activeLeadsGroup.items
    .filter((item) => !item.title || !hiddenNavTitles.has(item.title))
    .map(withStageIcon);

  return [
  {
    subheader: 'Patients',
    items: [
      // Single Dashboard entry for all practices — it always opens the Launchpad.
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
      },
      {
        title: 'Active Leads',
        path: PATH_DASHBOARD.tables.newLeadTable,
        icon: ICONS.user,
        children: activeLeadsChildren,
      },
      {
        title: 'Lead Management',
        path: PATH_DASHBOARD.tables.archiveLeadTable,
        icon: ICONS.leadManagement,
        children: [
          ...leadManagementGroup.items,
          ...(hasMultiplePractices ? [{ divider: true }, { title: 'All Practice Leads', icon: STAGE_ICONS.ALL_PRACTICES, path: PATH_DASHBOARD.tables.InterPractice }] : []),
          // Attendance review: super-admin only (Raj, 2026-10-23) AND only where the practice has attendance capture
          // enabled (PracticePreferences.attendance_capture_eligible, backend #7084) — see src/utils/attendanceUiFlag.js.
          ...(isSuperAdmin && hasAttendanceUi ? [{ divider: true }, { title: 'Attendance review', icon: ICONS.calendar, path: PATH_DASHBOARD.tables.attendanceReview }] : []),
        ].filter((item) => !item.title || !hiddenNavTitles.has(item.title)).map(withStageIcon),
      },
      ...(hasOwnDiary ? [{title:'My diary',path:PATH_DASHBOARD.user.myDiary,icon:ICONS.calendar}] : []),
      { title: 'Appointments', path: PATH_DASHBOARD.user.scheduling, icon: ICONS.calendar, feature: 'pms-scheduling', requirePermission: true },
      { title: 'Dentally', path: PATH_DASHBOARD.tables.dentallyPatients, icon: <Iconify icon="medical-icon:i-dental" width={24} />, feature: 'dentally-integration' },
      // Task O addendum: "Tasks" -> "Task Board" (matches the Launchpad panel's
      // "Task Board · today" label/link and the tasks-v2 page's own breadcrumb/title).
      { title: 'Task Board', path: hasTasksV2 ? PATH_DASHBOARD.task.v2Root : PATH_DASHBOARD.task.root, icon: ICONS.task, info: hasTasksV2 ? undefined : <TaskBadge /> },
      { title: 'Forms', path: PATH_DASHBOARD.forms.root, icon: ICONS.invoice, feature: 'forms-v2', requirePermission: true },
    ],
  },
  {
    subheader: 'Communication',
    items: [
      { title: 'WhatsApp', path: PATH_DASHBOARD.whatsapp.root(practiceId), icon: ICONS.whatsapp, feature: 'whatsapp-messaging', info: <WhatsappBadge /> },
      // Call Logs promoted out of "More" to sit directly under WhatsApp (user request
      // 2026-10-04). Icon = the CRM's shared phone channel glyph (lastContact.js PHONE).
      { title: 'Call Logs', path: PATH_DASHBOARD.callLogs.root, icon: <Iconify icon="mdi:phone-outline" width={20} />, feature: 'call-logs' },
      {
        title: 'More',
        path: PATH_DASHBOARD.emails.root,
        icon: <Iconify icon="eva:more-horizontal-fill" width={24} />,
        children: [
          { title: 'Emails', path: PATH_DASHBOARD.emails.root, feature: 'email-inbox' },
          { title: 'Facebook', path: PATH_DASHBOARD.user.facebookComment, feature: 'facebook-integration' },
          { title: 'Instagram', path: PATH_DASHBOARD.instagramDm.root(practiceId) },
        ],
      },
    ],
  },
  {
    subheader: 'Business',
    items: [
      {
        title: 'Reporting',
        path: PATH_DASHBOARD.analyticsReports.overview,
        icon: ICONS.analytics,
        feature: 'analytics-reports',
      },
      { title: 'Reviews & Feedback', path: PATH_DASHBOARD.manageBusiness, icon: <Iconify icon="logos:google-icon" width={18} />, feature: 'google-reviews' },
    ],
  },
  // STORAGE section temporarily hidden from the sidebar — the page
  // and route are intentionally retained so existing bookmarks and
  // deep-links keep working; only the nav entry is suppressed. Restore
  // by un-commenting this block once Storage is ready to surface again.
  // {
  //   subheader: 'Storage',
  //   items: [
  //     { title: 'Storage', path: PATH_DASHBOARD.storage.root, icon: <Iconify icon="solar:cloud-storage-bold-duotone" width={24} /> },
  //   ],
  // },
  {
    subheader: 'More Tools',
    items: [
      // ONE expandable "More": the old Business → More children (order kept), then the
      // former Marketing Tools items, then Website Annotation (previously top-level).
      // Children render with a dot, not an icon, so the moved items drop their icons.
      // Marketing Tracker + Campaign Config live in Reporting → More (AnalyticsReportsTabs.MORE_TABS).
      {
        title: 'More',
        path: PATH_DASHBOARD.financialTracker,
        icon: <Iconify icon="eva:more-horizontal-fill" width={24} />,
        children: [
          // Google Posts first (user request 2026-10-04).
          { title: 'Google Posts', path: PATH_DASHBOARD.googlePosts, feature: 'google-reviews' },
          // Legacy Analytics surface — super-admin-only entry point until
          // the new analytics-reports tabs fully supersede it.
          ...(isSuperAdmin ? [{ title: 'Analytics', path: PATH_DASHBOARD.analytics.reports }] : []),
          { title: 'Online Bookings', path: PATH_DASHBOARD.analytics.pmsBookingAnalytics, feature: 'pms-scheduling' },
          { title: 'Financial Tracker', path: PATH_DASHBOARD.financialTracker, feature: 'financial-tracker' },
          { title: 'QR Codes', path: PATH_DASHBOARD.qrCodeGenerator, feature: 'qr-code-generator' },
          { title: 'Survey', path: PATH_DASHBOARD.survey.root },
          // Marketing Calendar / Campaigns Manager collapse their former nested children into
          // one item each (pages render their own tabs). Both gated by `email-campaigns`.
          { title: 'Marketing Calendar', path: PATH_DASHBOARD.planner, feature: 'email-campaigns' },
          { title: 'Campaigns Manager', path: PATH_DASHBOARD.campaign.campaign, feature: 'email-campaigns' },
          // Website annotation is always visible to anyone with the feature (not hidden) —
          // sessions live in our backend; the session picker handles "no website" gracefully.
          { title: 'Website Annotation', path: PATH_DASHBOARD.websiteAnnotation, info: <DraftSessionBadge />, feature: 'website-annotation' },
        ],
      },
    ],
  },
];
};

export default getNavConfig;
